import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';

const translations = {
  nl: {
    files: 'Bestanden',
    searchPlaceholder: 'Zoek op bestandsnaam...',
    userSearchPlaceholder: 'Zoek op gebruiker...',
    showAll: 'Toon alle bestanden',
    download: 'Downloaden',
    openFile: 'Bestand openen',
    openChat: 'Open chat',
    by: 'door',
    uploaded: 'Geüpload',
    chat: 'Chat',
    noFiles: 'Nog geen bestanden geüpload.',
    loading: 'Meer bestanden laden...',
    deleteFile: 'Bestand verwijderen',
    redactFile: 'Bestand redigeren',
    moderationReason: 'Geef AVG/GDPR reden op',
    moderationFailed: 'Bestand modereren is mislukt',
  },
};

const getLang = (): 'nl' => 'nl';
const t = (key: keyof typeof translations.nl) => translations[getLang()][key];

interface File {
  _id: string | null;
  sender: string | null;
  senderName: string | null;
  fileName: string | null;
  fileUrl: string | null;
  fileSize: number | null;
  fileType: string | null;
  createdAt: string | null;
  sessionId: string | null;
}

interface FilesResponse {
  files: (File | null)[];
  totalFiles: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  pagination?: {
    page: number;
    pages: number;
    total: number;
  };
}

interface FilesViewProps {
  selectedUserId?: string;
  adminToken: string;
  setSelectedUser: (user: { id: string; name: string } | null) => void;
  fetchMessagesForUser: (userId: string, pageNum?: number) => void;
  setActiveSection: (section: 'chat' | 'files') => void;
}

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  color: #23272f;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 220px;
`;

const HeaderControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const UserSearchWrap = styled.div`
  position: relative;
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 110%;
  left: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const UserDropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
`;

const UserDropdownName = styled.div`
  font-weight: 500;
`;

const UserDropdownId = styled.div`
  font-size: 12px;
  color: #888;
`;

const ClearUserFilterButton = styled.button`
  margin-left: 8px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  background: #f3f4f6;
  cursor: pointer;
  font-weight: 500;
`;

const ListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
`;

const FilesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const FilesTableHead = styled.thead`
  background: #f8fafc;
`;

const FilesTableHeader = styled.th`
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  padding: 10px;
  border-bottom: 1px solid #e2e8f0;
`;

const FilesTableRow = styled.tr`
  &:nth-child(even) {
    background: #fcfdff;
  }
`;

const FilesTableCell = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  color: #1f2937;
  vertical-align: top;
`;

const FileNameText = styled.div`
  font-weight: 600;
`;

const FileSubText = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
`;

const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background-color: #fff;
  color: #374151;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.15s;
  min-width: 34px;
  max-width: 38px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &.primary {
    background-color: #007bff;
    border-color: #007bff;
    color: #fff;

    &:hover {
      background-color: #0056b3;
      border-color: #0056b3;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 1.2rem;
  color: #6c757d;
`;

const STATIC_URL =
  process.env.REACT_APP_API_URL ||
  (globalThis as any).supportlyChatConfig?.staticUrl ||
  '';

const getFileUrl = (url?: string | null, mimeType?: string | null) => {
  if (!url) return '';
  const addInlineIfNeeded = (value: string) => {
    if (!mimeType?.startsWith('image/')) return value;
    if (value.includes('inline=')) return value;
    return `${value}${value.includes('?') ? '&' : '?'}inline=1`;
  };

  if (url.startsWith('/uploads/')) {
    const filename = url.split('/').pop();
    const base =
      (globalThis as any).supportlyChatConfig?.staticUrl ||
      process.env.REACT_APP_API_URL ||
      globalThis.location.origin;
    const fileUrl = base.replace(/\/$/, '') + '/api/messages/download/' + filename;
    return addInlineIfNeeded(fileUrl);
  }

  if (url.startsWith('/api/messages/download/') || url.includes('/api/messages/download/')) {
    const apiBase = (
      (globalThis as any).supportlyChatConfig?.staticUrl ||
      process.env.REACT_APP_API_URL ||
      globalThis.location.origin
    ).replace(/\/$/, '');
    const pathSep = url.startsWith('/') ? '' : '/';
    const absolute = url.startsWith('http')
      ? url
      : `${apiBase}${pathSep}${url}`;
    return addInlineIfNeeded(absolute);
  }

  return url;
};

export const FilesView: React.FC<FilesViewProps> = ({
  selectedUserId,
  adminToken,
  setSelectedUser,
  fetchMessagesForUser,
  setActiveSection,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userList, setUserList] = useState<{ id: string; name: string }[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalFiles, setTotalFiles] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const searchTermRef = useRef(searchTerm);
  const userSearchTermRef = useRef(userSearchTerm);
  const selectedUserIdRef = useRef(selectedUserId);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    userSearchTermRef.current = userSearchTerm;
  }, [userSearchTerm]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  const normalizeFile = (raw: any): File => ({
    _id: raw?._id ?? null,
    sender: raw?.sender ?? null,
    senderName: typeof raw?.senderName === 'string' ? raw.senderName : null,
    fileName: typeof raw?.fileName === 'string' ? raw.fileName : null,
    fileUrl: typeof raw?.fileUrl === 'string' ? raw.fileUrl : null,
    fileSize: typeof raw?.fileSize === 'number' ? raw.fileSize : null,
    fileType: typeof raw?.fileType === 'string' ? raw.fileType : null,
    createdAt: typeof raw?.createdAt === 'string' ? raw.createdAt : null,
    sessionId: typeof raw?.sessionId === 'string' ? raw.sessionId : null,
  });

  const sortByNewestFirst = (list: File[]) =>
    [...list].sort((a, b) => {
      const aTs = new Date(a.createdAt || 0).getTime();
      const bTs = new Date(b.createdAt || 0).getTime();
      return bTs - aTs;
    });

  useEffect(() => {
    const fetchUsersAndSessions = async () => {
      try {
        const headers: HeadersInit | undefined = adminToken
          ? { Authorization: `Bearer ${adminToken}` }
          : undefined;

        const [usersRes, sessionsRes] = await Promise.all([
          fetch(`${STATIC_URL}/api/admin/users`, { headers }),
          fetch(`${STATIC_URL}/api/messages/sessions`, { headers }),
        ]);

        const users = await usersRes.json();
        const sessions = await sessionsRes.json();

        const regUsers = Array.isArray(users)
          ? users.map((u: any) => ({ id: u._id || u.id, name: u.name || u._id || '-' }))
          : [];

        const anonUsers = Array.isArray(sessions)
          ? sessions.map((s: any) => ({ id: s._id, name: s.userName || s._id || '-' }))
          : [];

        const regUserIds = new Set(regUsers.map((r: any) => r.id));
        const all = [...regUsers, ...anonUsers.filter((a: any) => !regUserIds.has(a.id))];
        setUserList(all);
      } catch (error) {
        console.warn('[FilesView] fetchUsersAndSessions error:', error);
        setUserList([]);
      }
    };

    fetchUsersAndSessions();
  }, [adminToken]);

  const fetchFiles = useCallback(
    async (page = 1, search = '', userId?: string, userSearch?: string, append = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '20',
          ...(search && { search }),
        });

        let url: string;
        if (userId) {
          url = `${STATIC_URL}/api/files?${params}&userId=${userId}`;
        } else if (userSearch) {
          url = `${STATIC_URL}/api/files?${params}&userSearch=${encodeURIComponent(userSearch)}`;
        } else {
          url = `${STATIC_URL}/api/files?${params}`;
        }

        const headers: HeadersInit | undefined = adminToken
          ? { Authorization: `Bearer ${adminToken}` }
          : undefined;

        const response = await fetch(url, { headers });
        const data: FilesResponse = await response.json();

        const rawFiles = Array.isArray(data.files) ? data.files : [];
        const newFiles = rawFiles
          .filter((item) => item && typeof item === 'object')
          .map((item) => normalizeFile(item));

        if (append) {
          setFiles((prev) => {
            const merged = [...prev, ...newFiles];
            const deduped = merged.filter(
              (item, index, arr) =>
                arr.findIndex((candidate) =>
                  `${candidate._id || ''}|${candidate.fileName || ''}|${candidate.createdAt || ''}` ===
                  `${item._id || ''}|${item.fileName || ''}|${item.createdAt || ''}`
                ) === index
            );
            return sortByNewestFirst(deduped);
          });
          setCurrentPage(page);
        } else {
          setFiles(sortByNewestFirst(newFiles));
          setCurrentPage(page);
        }

        setHasMore(newFiles.length === 20);
        setTotalFiles(data.pagination?.total || 0);
      } catch (error) {
        console.error('Error fetching files:', error);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [adminToken]
  );

  const debouncedFetchFiles = useRef(
    debounce((search: string, userId?: string, userSearch?: string) => {
      fetchFiles(1, search, userId, userSearch, false);
    }, 500)
  ).current;

  useEffect(() => {
    setCurrentPage(1);
    setFiles([]);
    setHasMore(true);
    debouncedFetchFiles(
      searchTerm,
      selectedUserId || undefined,
      selectedUserId ? undefined : userSearchTerm
    );
  }, [searchTerm, selectedUserId, userSearchTerm, debouncedFetchFiles]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loading && !isFetchingRef.current) {
        fetchFiles(
          currentPage + 1,
          searchTermRef.current,
          selectedUserIdRef.current || undefined,
          selectedUserIdRef.current ? undefined : userSearchTermRef.current,
          true
        );
      }
    },
    [currentPage, hasMore, loading, fetchFiles]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDownload = (file: File) => {
    const link = document.createElement('a');
    link.href = getFileUrl(file.fileUrl, file.fileType);
    link.download = file.fileName || 'attachment';
    link.click();
  };

  const handleOpen = (file: File) => {
    window.open(getFileUrl(file.fileUrl, file.fileType), '_blank');
  };

  const handleModerate = async (file: File, action: 'delete' | 'redact') => {
    if (!file._id) {
      alert(t('moderationFailed'));
      return;
    }

    const reason = globalThis.prompt(`${t('moderationReason')}:`);
    if (!reason?.trim()) return;

    try {
      const headers: HeadersInit = {
        Authorization: `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(
        `${STATIC_URL}/api/admin/files/${encodeURIComponent(file._id)}/moderate`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ action, reason: reason.trim() }),
        }
      );

      if (!response.ok) throw new Error('moderation-failed');

      fetchFiles(
        1,
        searchTermRef.current,
        selectedUserIdRef.current || undefined,
        selectedUserIdRef.current ? undefined : userSearchTermRef.current,
        false
      );
    } catch (error) {
      console.warn('[FilesView] Moderation error:', error);
      alert(t('moderationFailed'));
    }
  };

  const handleOpenChat = (file: File) => {
    if (!file.sessionId) return;
    setActiveSection('chat');
    const user = userList.find((u) => u.id === file.sessionId);
    setSelectedUser({ id: file.sessionId, name: user?.name || file.senderName || file.sessionId });
    fetchMessagesForUser(file.sessionId);
  };

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const filteredUserList = useMemo(() => {
    if (!userSearchTerm) return [];
    const term = userSearchTerm.toLowerCase();
    return userList.filter(
      (u) => (u.name || '').toLowerCase().includes(term) || (u.id || '').toLowerCase().includes(term)
    );
  }, [userSearchTerm, userList]);

  const handleUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSearchTerm(e.target.value);
    setShowUserDropdown(true);
  };

  const handleUserSelect = (user: { id: string; name: string }) => {
    setSelectedUser(user);
    setShowUserDropdown(false);
    setUserSearchTerm(user.name);
  };

  const getPlayerName = (file: File) => {
    if (file.sessionId) {
      const found = userList.find((u) => u.id === file.sessionId);
      if (found?.name) return found.name;
    }
    return file.senderName || '-';
  };

  return (
    <Main className="admin-files-view-main">
      <Header className="admin-files-view-header">
        <Title className="admin-files-view-title">
          {t('files')} {totalFiles ? `(${totalFiles})` : ''}
        </Title>
        <HeaderControls className="admin-files-view-controls">
          <SearchInput
            className="admin-files-search-input"
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearch}
          />
          <UserSearchWrap className="admin-files-user-search-wrap">
            <SearchInput
              className="admin-files-user-search-input"
              type="text"
              placeholder={t('userSearchPlaceholder')}
              value={userSearchTerm}
              onChange={handleUserSearch}
              onFocus={() => setShowUserDropdown(true)}
            />
            {showUserDropdown && filteredUserList.length > 0 && (
              <UserDropdown className="admin-files-user-dropdown">
                {filteredUserList.map((u) => (
                  <UserDropdownItem key={u.id} onClick={() => handleUserSelect(u)}>
                    <UserDropdownName>{u.name}</UserDropdownName>
                    <UserDropdownId>{u.id}</UserDropdownId>
                  </UserDropdownItem>
                ))}
              </UserDropdown>
            )}
          </UserSearchWrap>
          {selectedUserId && (
            <ClearUserFilterButton
              className="admin-files-clear-user-filter"
              onClick={() => {
                setSelectedUser(null);
                setUserSearchTerm('');
              }}
            >
              {t('showAll')}
            </ClearUserFilterButton>
          )}
        </HeaderControls>
      </Header>

      <ListContainer
        className="admin-files-list-container"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {files.length === 0 && !loading ? (
          <EmptyState className="admin-files-empty-state">{t('noFiles')}</EmptyState>
        ) : (
          <FilesTable className="admin-files-table">
            <FilesTableHead>
              <tr>
                <FilesTableHeader>Bestandsnaam</FilesTableHeader>
                <FilesTableHeader>Speler</FilesTableHeader>
                <FilesTableHeader>Speler ID / UUID</FilesTableHeader>
                <FilesTableHeader>Type / Grootte</FilesTableHeader>
                <FilesTableHeader>Geupload op</FilesTableHeader>
                <FilesTableHeader>Acties</FilesTableHeader>
              </tr>
            </FilesTableHead>
            <tbody>
              {files.map((file) => {
                const fileName = file.fileName || 'Onbekend bestand';
                const sessionId = file.sessionId || '-';
                return (
                  <FilesTableRow
                    className="admin-files-row"
                    key={file._id || `${sessionId}-${file.createdAt || 'unknown'}`}
                  >
                    <FilesTableCell className="admin-files-cell admin-files-cell-name">
                      <FileNameText>{fileName}</FileNameText>
                    </FilesTableCell>
                    <FilesTableCell className="admin-files-cell admin-files-cell-player-name">
                      {getPlayerName(file)}
                    </FilesTableCell>
                    <FilesTableCell className="admin-files-cell admin-files-cell-player-id">
                      {sessionId}
                    </FilesTableCell>
                    <FilesTableCell className="admin-files-cell admin-files-cell-file-meta">
                      <FileSubText>{file.fileType || '-'}</FileSubText>
                      <FileSubText>{formatFileSize(file.fileSize)}</FileSubText>
                    </FilesTableCell>
                    <FilesTableCell className="admin-files-cell admin-files-cell-created-at">
                      {formatDate(file.createdAt)}
                    </FilesTableCell>
                    <FilesTableCell className="admin-files-cell admin-files-cell-actions">
                      <FileActions>
                        <ActionButton onClick={() => handleDownload(file)} title={t('download')}>⬇️</ActionButton>
                        <ActionButton onClick={() => handleOpen(file)} title={t('openFile')}>👁️</ActionButton>
                        <ActionButton className="primary" onClick={() => handleOpenChat(file)} title={t('openChat')}>💬</ActionButton>
                        <ActionButton onClick={() => handleModerate(file, 'redact')} title={t('redactFile')}>✏️</ActionButton>
                        <ActionButton onClick={() => handleModerate(file, 'delete')} title={t('deleteFile')}>🗑️</ActionButton>
                      </FileActions>
                    </FilesTableCell>
                  </FilesTableRow>
                );
              })}
            </tbody>
          </FilesTable>
        )}
        {loading && <LoadingState className="admin-files-loading-state">{t('loading')}</LoadingState>}
      </ListContainer>
    </Main>
  );
};
